import React, { useState } from 'react';
import { Cpu, Check, ArrowRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import QuoteModal from './QuoteModal';
import { Button } from './ui/button';
import { products, productCategories } from '../data/shop';

const ShopPage = () => {
  const [category, setCategory] = useState('All');
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const filtered = category === 'All' ? products : products.filter((p) => p.category === category);

  const openQuote = (product) => {
    setQuoteProduct(product);
    setQuoteOpen(true);
  };

  return (
    <>
      <Header />
      <main className="pt-[70px]">
        {/* Page hero */}
        <section className="bg-gradient-to-br from-blue-800 to-blue-900 py-16">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
            <span className="inline-flex items-center gap-2 bg-cyan-400/20 border border-cyan-300/40 text-cyan-100 text-sm font-medium px-4 py-1.5 rounded-full">
              <Cpu size={16} /> Shop Computers
            </span>
            <h1 className="mt-5 font-heading font-extrabold text-white text-4xl md:text-5xl">Premium Computers for Every Need</h1>
            <p className="mt-4 text-blue-100 text-lg max-w-2xl mx-auto">
              Hand-picked laptops, desktops, and gaming rigs — expertly configured and backed by our support team. Request a quote and we'll get back to you fast.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="py-12 bg-slate-50 min-h-[60vh]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex flex-wrap gap-2 justify-center mb-10">
              {productCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    category === cat
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filtered.map((p, i) => (
                <div
                  key={p.id}
                  className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col animate-fade-up"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {p.badge && (
                      <span className="absolute top-3 left-3 bg-cyan-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">{p.badge}</span>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">{p.category}</span>
                    <h3 className="mt-1 font-heading font-semibold text-slate-900 text-lg leading-snug">{p.name}</h3>
                    <ul className="mt-3 space-y-1.5 flex-1">
                      {p.specs.map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-slate-500">
                          <Check size={14} className="text-blue-500 mt-0.5 shrink-0" /> {s}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-5 flex items-center justify-between">
                      <span className="font-heading font-extrabold text-slate-900 text-2xl">{p.price}</span>
                    </div>
                    <Button onClick={() => openQuote(p)} className="mt-4 w-full bg-slate-900 hover:bg-blue-600 text-white h-11 rounded-lg font-semibold transition-colors">
                      Request Quote <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <QuoteModal open={quoteOpen} onOpenChange={setQuoteOpen} product={quoteProduct} />
    </>
  );
};

export default ShopPage;
